/**
 * LiveViewPage.tsx — 투자자 라이브 투어 시청 페이지
 * Route: /listings/:id/live
 * 중개사가 방송하는 매물 현장을 실시간으로 시청
 */

import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWebRTC } from '../../hooks/useWebRTC';
import { useLiveTour } from './useLiveTour';

export default function LiveViewPage() {
  const { id: listingId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const rtc  = useWebRTC();
  const tour = useLiveTour(listingId);

  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const [chatInput, setChatInput]   = useState('');
  const [connected, setConnected]   = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // 라이브 스트림에 참여
  useEffect(() => {
    if (!tour.isLive || connected) return;

    // 오디오만 있는 더미 스트림으로 joinRoom (시청만 → 영상은 remote로만 받음)
    navigator.mediaDevices.getUserMedia({ video: false, audio: false })
      .then(async stream => {
        await rtc.joinRoom(listingId, stream, 'land-tours-rtc');
        tour.joinAsViewer();
        setConnected(true);
      })
      .catch(async () => {
        // 권한 없어도 원격 스트림 수신 가능하도록 빈 MediaStream 사용
        const emptyStream = new MediaStream();
        await rtc.joinRoom(listingId, emptyStream, 'land-tours-rtc');
        tour.joinAsViewer();
        setConnected(true);
      });

    return () => {
      tour.leaveAsViewer();
      rtc.hangup(listingId, 'land-tours-rtc');
    };
  }, [tour.isLive]);

  // 원격 스트림 연결
  useEffect(() => {
    if (remoteVideoRef.current && rtc.remoteStream) {
      remoteVideoRef.current.srcObject = rtc.remoteStream;
    }
  }, [rtc.remoteStream]);

  // 채팅 스크롤
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [tour.chatMessages]);

  const handleSendChat = () => {
    if (!chatInput.trim()) return;
    tour.sendChat(chatInput.trim(), 'viewer');
    setChatInput('');
  };

  // 라이브 아님
  if (!tour.isLive) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0a0a12',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        color: '#f1f5f9', gap: 16,
      }}>
        <div style={{ fontSize: '3rem' }}>📡</div>
        <p style={{ fontSize: '1.1rem', fontWeight: 700 }}>현재 라이브 투어가 없습니다</p>
        <p style={{ color: '#64748b', fontSize: '0.85rem' }}>중개사가 라이브를 시작하면 자동으로 연결됩니다.</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px', borderRadius: 8, border: '1px solid #374151',
              background: 'transparent', color: '#94a3b8', cursor: 'pointer',
            }}
          >
            새로고침
          </button>
          <button
            onClick={() => navigate(`/listings/${listingId}`)}
            style={{
              padding: '10px 20px', borderRadius: 8, border: 'none',
              background: '#1d4ed8', color: '#fff', cursor: 'pointer', fontWeight: 700,
            }}
          >
            매물로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#000', color: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>
      {/* 헤더 */}
      <div style={{
        padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(0,0,0,0.8)',
      }}>
        <button onClick={() => navigate(`/listings/${listingId}`)}
          style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.2rem' }}>
          ←
        </button>
        <span style={{ fontWeight: 700 }}>매물 라이브 투어</span>
        <span style={{
          background: '#dc2626', color: '#fff', borderRadius: 20,
          padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', display: 'inline-block',
            animation: 'pulse 1s infinite' }} />
          LIVE
        </span>
        <span style={{ color: '#64748b', fontSize: '0.8rem' }}>👁 {tour.viewerCount}명</span>

        {rtc.connState === 'connecting' && (
          <span style={{ color: '#f59e0b', fontSize: '0.75rem' }}>연결 중...</span>
        )}
      </div>

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 300px', minHeight: 0 }}>
        {/* 영상 */}
        <div style={{ position: 'relative', background: '#000' }}>
          <video
            ref={remoteVideoRef}
            autoPlay playsInline
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />

          {!rtc.remoteStream && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 12,
            }}>
              <div style={{
                width: 48, height: 48, border: '4px solid #374151',
                borderTopColor: '#f97316', borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }} />
              <p style={{ color: '#64748b', fontSize: '0.85rem' }}>스트림 수신 중...</p>
            </div>
          )}
        </div>

        {/* 채팅 */}
        <div style={{
          borderLeft: '1px solid #1e2030', background: '#0d1117',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #1e2030', fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>
            실시간 채팅 — 질문을 남겨보세요
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tour.chatMessages.map(m => (
              <div key={m.id} style={{
                alignSelf: m.sender === 'viewer' ? 'flex-end' : 'flex-start',
                background: m.sender === 'broker' ? '#1c3a5c' : '#1e2030',
                borderRadius: 10, padding: '6px 12px',
                fontSize: '0.8rem', color: '#f1f5f9', maxWidth: '80%',
              }}>
                {m.sender === 'broker' && (
                  <div style={{ fontSize: '0.65rem', color: '#60a5fa', marginBottom: 2 }}>중개사</div>
                )}
                {m.text}
              </div>
            ))}
            {tour.chatMessages.length === 0 && (
              <p style={{ color: '#374151', fontSize: '0.75rem', textAlign: 'center', marginTop: 20 }}>
                채팅을 시작해보세요
              </p>
            )}
            <div ref={chatEndRef} />
          </div>

          <div style={{ padding: 10, borderTop: '1px solid #1e2030', display: 'flex', gap: 6 }}>
            <input
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendChat()}
              placeholder="질문 입력..."
              style={{
                flex: 1, padding: '8px 10px', borderRadius: 6,
                border: '1px solid #374151', background: '#111827',
                color: '#f1f5f9', fontSize: '0.8rem',
              }}
            />
            <button
              onClick={handleSendChat}
              style={{
                padding: '8px 12px', borderRadius: 6, border: 'none',
                background: '#1d4ed8', color: '#fff', fontWeight: 700, cursor: 'pointer',
              }}
            >
              전송
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
